import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Paper,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
} from "@mui/material";
import PageHeader from "../../components/PageHeader";
import HistoryIcon from "@mui/icons-material/History";
import VisibilityIcon from "@mui/icons-material/Visibility";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import SchoolIcon from "@mui/icons-material/School";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import IntegrityIcon from "@mui/icons-material/VerifiedUser";
import ExcellenceIcon from "@mui/icons-material/EmojiEvents";
import RespectIcon from "@mui/icons-material/Diversity3";
import InnovationIcon from "@mui/icons-material/Lightbulb";
import CommitmentIcon from "@mui/icons-material/HandshakeOutlined";
import ResponsibilityIcon from "@mui/icons-material/AccountBalance";

const QuienesSomos = () => {
  const navigate = useNavigate();

  // References for internal navigation
  const historyRef = useRef(null);
  const missionRef = useRef(null);
  const valuesRef = useRef(null);
  const servicesRef = useRef(null);
  const academicRef = useRef(null);

  // Smooth scroll function
  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Sidebar navigation
  const sidebarSections = [
    {
      id: "history",
      label: "Our Story",
      ref: historyRef,
      icon: <HistoryIcon />,
    },
    {
      id: "mission",
      label: "Mission & Vision",
      ref: missionRef,
      icon: <VisibilityIcon />,
    },
    {
      id: "values",
      label: "Institutional Values",
      ref: valuesRef,
      icon: <FavoriteBorderIcon />,
    },
    {
      id: "services",
      label: "Services by Area",
      ref: servicesRef,
      icon: <BusinessCenterIcon />,
    },
    {
      id: "academic",
      label: "Academic Proposal",
      ref: academicRef,
      icon: <SchoolIcon />,
    },
  ];

  // Institutional values
  const institutionalValues = [
    {
      icon: <CommitmentIcon />,
      title: "Commitment",
      description:
        "We commit to providing high-quality education, actively participating in community development, and maintaining a collaborative and transparent work environment.",
      color: "#2B2B6B",
    },
    {
      icon: <RespectIcon />,
      title: "Respect",
      description:
        "We promote respect as a fundamental pillar of the teaching-learning process, fostering a horizontal relationship with the entire educational community.",
      color: "#E31E24",
    },
    {
      icon: <IntegrityIcon />,
      title: "Loyalty",
      description:
        "We maintain a commitment to education, the educational community, and its institution by promoting values such as accessibility, innovation, social impact, and academic quality.",
      color: "#2B2B6B",
    },
    {
      icon: <IntegrityIcon />,
      title: "Integrity",
      description:
        "We act with honesty and consistency, upholding ethical and moral principles in all our actions.",
      color: "#E31E24",
    },
    {
      icon: <ExcellenceIcon />,
      title: "Excellence",
      description:
        "We develop the full individual and collective potential of our students through continuous improvement and innovation, maintaining the highest ethical and professional standards.",
      color: "#2B2B6B",
    },
    {
      icon: <BusinessCenterIcon />,
      title: "Service",
      description:
        "Rooted in our capacity to offer accessible, relevant, and socially responsible education that empowers students to achieve personal and professional goals.",
      color: "#E31E24",
    },
    {
      icon: <InnovationIcon />,
      title: "Leadership",
      description:
        "We foster ethical and transformative leadership, oriented toward generating a positive impact both in the academic community and in the business environment.",
      color: "#2B2B6B",
    },
    {
      icon: <ResponsibilityIcon />,
      title: "Responsibility",
      description:
        "We are capable of assuming personal and collective obligations and commitments, acting correctly and honoring shared goals with ourselves, others, and the environment.",
      color: "#E31E24",
    },
    {
      icon: <RespectIcon />,
      title: "Inclusivity",
      description:
        "We commit to guaranteeing the right of all people to a quality education, based on respect, recognition, and appreciation of diversity and individual differences.",
      color: "#2B2B6B",
    },
  ];

  // Services by area
  const servicesByArea = [
    {
      area: "Academic Services",
      services: [
        "Scholarship Program",
        "Pathway Program",
        "Graduation Seminar",
        "Social Service and Internships",
      ],
      color: "#2B2B6B",
    },
    {
      area: "Internationalization Services",
      services: [
        "Agreements with Foreign Universities",
        "Study Abroad Counseling",
        "International Admission Requirements",
        "Guidance on Living Abroad",
      ],
      color: "#E31E24",
    },
    {
      area: "Student Support Services",
      services: [
        "Student Wellness",
        "Entrepreneurship Program",
        "Events and Activities",
        "Student Organization",
      ],
      color: "#00CED1",
    },
    {
      area: "Resources and Library",
      services: [
        "Physical and Digital Library",
        "Computer Center",
        "Advertising and Media",
        "Research",
      ],
      color: "#FF6B35",
    },
  ];

  // Academic programs
  const academicPrograms = [
    {
      title: "Business Administration",
      description:
        "Trains professionals capable of managing organizations with strategic vision and leadership.",
      icon: <BusinessCenterIcon sx={{ fontSize: 40 }} />,
      duration: "2.5 years",
      credits: "90 credits",
    },
    {
      title: "Liberal Arts",
      description:
        "Develops critical thinking and interdisciplinary skills for a globalized world.",
      icon: <SchoolIcon sx={{ fontSize: 40 }} />,
      duration: "2 years",
      credits: "72 credits",
    },
  ];

  return (
    <>
      <PageHeader
        title="Who We Are"
        subtitle="Learn about our history, mission, values, and everything that makes us American College."
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "About Us", href: "/about-us" },
          { label: "Who We Are", href: "/about-us/quienes-somos" },
        ]}
      />

      <Container maxWidth="xl" sx={{ mb: 8, mt: 4 }}>
        <Grid container spacing={4}>
          {/* Sidebar Navigation */}
          <Grid item xs={12} md={3}>
            <Paper
              elevation={0}
              sx={{
                position: "sticky",
                top: 100,
                border: "1px solid #E0E0E0",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  background:
                    "linear-gradient(135deg, #2B2B6B 0%, #1a1a4a 100%)",
                  color: "white",
                  p: 2.5,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontSize: "1.1rem", fontWeight: 600 }}
                >
                  In this Section
                </Typography>
              </Box>
              <List sx={{ py: 1 }}>
                {sidebarSections.map((section) => (
                  <ListItem key={section.id} disablePadding>
                    <ListItemButton
                      onClick={() => scrollToSection(section.ref)}
                      sx={{
                        py: 1.5,
                        px: 2,
                        "&:hover": {
                          backgroundColor: "rgba(43, 43, 107, 0.08)",
                          borderLeft: "4px solid #00CED1",
                        },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 40, color: "#2B2B6B" }}>
                        {section.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={section.label}
                        primaryTypographyProps={{
                          fontSize: "0.9rem",
                          fontWeight: 500,
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>

          {/* Main Content */}
          <Grid item xs={12} md={9}>
            {/* HISTORY */}
            <Box ref={historyRef} sx={{ mb: 8, scrollMarginTop: "100px" }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Avatar
                  sx={{
                    backgroundColor: "#2B2B6B",
                    width: 50,
                    height: 50,
                    mr: 2,
                  }}
                >
                  <HistoryIcon />
                </Avatar>
                <Typography
                  variant="h4"
                  sx={{
                    color: "#2B2B6B",
                    fontWeight: 700,
                    fontSize: { xs: "1.75rem", md: "2rem" },
                  }}
                >
                  Our Story
                </Typography>
              </Box>

              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  border: "1px solid #E0E0E0",
                  borderRadius: 2,
                  background:
                    "linear-gradient(135deg, rgba(43,43,107,0.02) 0%, rgba(255,255,255,1) 100%)",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: "#555",
                    fontSize: "1.05rem",
                    lineHeight: 1.8,
                    mb: 3,
                  }}
                >
                  If you're reading a bit about our history, let me tell you
                  that you've chosen the best option for your studies. We have
                  experience, prestige, and partnerships that will benefit your
                  professional growth. American College was founded in 1974 when
                  parents and the directors of the American School Association
                  in El Salvador expressed interest in establishing the first
                  two years of a university program taught in English. The idea
                  was to create an accredited liberal arts program that could be
                  expanded into a five-year university. Therefore, since its
                  founding, with the vision of becoming the first institution in
                  El Salvador to open international pathways for bilingual
                  students, facilitating their academic advancement abroad.
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: "#555",
                    fontSize: "1.05rem",
                    lineHeight: 1.8,
                    mb: 3,
                  }}
                >
                  Its fundamental purpose is to train highly qualified
                  professionals whose skills and leadership contribute to the
                  development and positive transformation of El Salvador. Below
                  you will find a timeline with the following: In August 1978,
                  an agreement was signed between San Jose State University in
                  California, USA, and the American School, in which San Jose
                  State University committed to offering, under its sponsorship,
                  at the American School's facilities, a program with courses
                  taken from the curriculum offered to first- and second-year
                  San Jose students. Upon completion of the program, students
                  could transfer to foreign universities to continue their
                  higher and/or professional studies.
                </Typography>
              </Paper>
            </Box>

            <Divider sx={{ my: 6 }} />

            {/* MISSION & VISION */}
            <Box ref={missionRef} sx={{ mb: 8, scrollMarginTop: "100px" }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Avatar
                  sx={{
                    backgroundColor: "#E31E24",
                    width: 50,
                    height: 50,
                    mr: 2,
                  }}
                >
                  <VisibilityIcon />
                </Avatar>
                <Typography
                  variant="h4"
                  sx={{
                    color: "#2B2B6B",
                    fontWeight: 700,
                    fontSize: { xs: "1.75rem", md: "2rem" },
                  }}
                >
                  Where We Are Heading
                </Typography>
              </Box>

              <Grid container spacing={3}>
                {/* Vision */}
                <Grid item xs={12} md={6}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      border: "2px solid #00CED1",
                      borderRadius: 2,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        background:
                          "linear-gradient(135deg, #2B2B6B 0%, #1a1a4a 100%)",
                        p: 3,
                        color: "white",
                      }}
                    >
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        Our Vision
                      </Typography>
                    </Box>
                    <CardContent sx={{ p: 4 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          color: "#555",
                          fontSize: "1rem",
                          lineHeight: 1.8,
                          fontStyle: "italic",
                          borderLeft: "4px solid #00CED1",
                          pl: 3,
                        }}
                      >
                        To be the leading English-speaking higher education
                        institution in El Salvador, offering innovative programs
                        that form professionals with a global perspective and
                        facilitate international transfers through the
                        implementation of cutting-edge methodologies and
                        transformative approaches.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Mission */}
                <Grid item xs={12} md={6}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      border: "2px solid #E31E24",
                      borderRadius: 2,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        background:
                          "linear-gradient(135deg, #E31E24 0%, #C41A1F 100%)",
                        p: 3,
                        color: "white",
                      }}
                    >
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        Our Mission
                      </Typography>
                    </Box>
                    <CardContent sx={{ p: 4 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          color: "#555",
                          fontSize: "1rem",
                          lineHeight: 1.8,
                          fontStyle: "italic",
                          borderLeft: "4px solid #E31E24",
                          pl: 3,
                        }}
                      >
                        To train professionals with international academic
                        standards who understand the national reality and are
                        committed to the socioeconomic transformation of the
                        country and the promotion of social coexistence.
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ my: 6 }} />

            <Box ref={valuesRef} sx={{ mb: 8, scrollMarginTop: "100px" }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Avatar
                  sx={{
                    backgroundColor: "#2B2B6B",
                    width: 50,
                    height: 50,
                    mr: 2,
                  }}
                >
                  <FavoriteBorderIcon />
                </Avatar>
                <Typography
                  variant="h4"
                  sx={{
                    color: "#2B2B6B",
                    fontWeight: 700,
                    fontSize: { xs: "1.75rem", md: "2rem" },
                  }}
                >
                  Institutional Values
                </Typography>
              </Box>

              <Typography
                variant="body1"
                sx={{
                  color: "#666",
                  fontSize: "1.05rem",
                  mb: 4,
                  lineHeight: 1.8,
                }}
              >
                Our values are the fundamental pillars that guide all our
                decisions and actions as an educational institution.
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                    md: "1fr 1fr 1fr",
                  },
                  gap: 2,
                }}
              >
                {institutionalValues.map((value, index) => (
                  <Card
                    key={index}
                    elevation={0}
                    sx={{
                      border: "1px solid #E0E0E0",
                      borderRadius: 2,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        background: value.color,
                        p: 2,
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                      }}
                    >
                      <Avatar
                        sx={{
                          backgroundColor: "rgba(255,255,255,0.2)",
                          color: "white",
                          width: 36,
                          height: 36,
                        }}
                      >
                        {value.icon}
                      </Avatar>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {value.title}
                      </Typography>
                    </Box>
                    <CardContent sx={{ p: 2.5 }}>
                      <Typography
                        variant="body2"
                        sx={{ color: "#666", lineHeight: 1.7 }}
                      >
                        {value.description}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Box>

            <Divider sx={{ my: 6 }} />

            {/* SERVICES BY AREA */}
            <Box ref={servicesRef} sx={{ mb: 8, scrollMarginTop: "100px" }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Avatar
                  sx={{
                    backgroundColor: "#FF6B35",
                    width: 50,
                    height: 50,
                    mr: 2,
                  }}
                >
                  <BusinessCenterIcon />
                </Avatar>
                <Typography
                  variant="h4"
                  sx={{
                    color: "#2B2B6B",
                    fontWeight: 700,
                    fontSize: { xs: "1.75rem", md: "2rem" },
                  }}
                >
                  Services by Area
                </Typography>
              </Box>

              <Typography
                variant="body1"
                sx={{
                  color: "#666",
                  fontSize: "1.05rem",
                  mb: 4,
                  lineHeight: 1.8,
                }}
              >
                We offer a wide range of services organized by areas to support
                your academic, professional, and personal development.
              </Typography>

              <Grid container spacing={3}>
                {servicesByArea.map((area, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <Card
                      elevation={0}
                      sx={{
                        height: "100%",
                        border: "1px solid #E0E0E0",
                        borderRadius: 2,
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          background: `linear-gradient(135deg, ${area.color} 0%, ${area.color}dd 100%)`,
                          p: 2.5,
                          color: "white",
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {area.area}
                        </Typography>
                      </Box>
                      <CardContent sx={{ p: 3 }}>
                        <List sx={{ p: 0 }}>
                          {area.services.map((service, idx) => (
                            <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 36 }}>
                                <CheckCircleOutlineIcon
                                  sx={{ color: area.color, fontSize: 20 }}
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={service}
                                primaryTypographyProps={{
                                  fontSize: "0.95rem",
                                  color: "#555",
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              <Box sx={{ mt: 4, textAlign: "center" }}>
                <Button
                  variant="outlined"
                  endIcon={<ArrowForwardIcon />}
                  sx={{
                    borderColor: "#2B2B6B",
                    color: "#2B2B6B",
                    textTransform: "none",
                    px: 4,
                    py: 1.5,
                    fontSize: "1rem",
                    fontWeight: 600,
                    borderRadius: 2,
                    "&:hover": {
                      borderColor: "#E31E24",
                      backgroundColor: "rgba(227,30,36,0.05)",
                      color: "#E31E24",
                    },
                  }}
                  onClick={() => navigate("/about-us/services")}
                >
                  View All Services
                </Button>
              </Box>
            </Box>

            <Divider sx={{ my: 6 }} />

            {/* ACADEMIC PROPOSAL */}
            <Box ref={academicRef} sx={{ mb: 6, scrollMarginTop: "100px" }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Avatar
                  sx={{
                    backgroundColor: "#2B2B6B",
                    width: 50,
                    height: 50,
                    mr: 2,
                  }}
                >
                  <SchoolIcon />
                </Avatar>
                <Typography
                  variant="h4"
                  sx={{
                    color: "#2B2B6B",
                    fontWeight: 700,
                    fontSize: { xs: "1.75rem", md: "2rem" },
                  }}
                >
                  Academic Proposal
                </Typography>
              </Box>

              <Typography
                variant="body1"
                sx={{
                  color: "#666",
                  fontSize: "1.05rem",
                  mb: 4,
                  lineHeight: 1.8,
                }}
              >
                Our Associate Degrees are designed to provide you with a solid
                foundation in your area of study and prepare you to continue at
                international universities or enter the labor market.
              </Typography>

              <Grid container spacing={3}>
                {academicPrograms.map((program, index) => (
                  <Grid item xs={12} md={4} key={index}>
                    <Card
                      elevation={0}
                      sx={{
                        width: "100%",
                        height: "100%",
                        border: "2px solid #E0E0E0",
                        borderRadius: 2,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "translateY(-8px)",
                          boxShadow: "0 12px 32px rgba(43,43,107,0.15)",
                          borderColor: "#2B2B6B",
                        },
                      }}
                    >
                      <CardContent sx={{ p: 4 }}>
                        <Box
                          sx={{
                            width: 72,
                            height: 72,
                            borderRadius: 2,
                            background:
                              "linear-gradient(135deg, #2B2B6B15 0%, #2B2B6B05 100%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 3,
                            color: "#2B2B6B",
                          }}
                        >
                          {program.icon}
                        </Box>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 700, mb: 2, color: "#2B2B6B" }}
                        >
                          {program.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#666", mb: 3, lineHeight: 1.6 }}
                        >
                          {program.description}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            flexWrap: "wrap",
                            flexGrow: 1,
                          }}
                        >
                          <Chip label={program.duration} size="small" />
                          <Chip
                            label={program.credits}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default QuienesSomos;
